import { useRef } from "react";
import { useReactToPrint } from "react-to-print";

import type {
  UserTestDto,
  UserTestPerformanceDto,
} from "~/data/user-test/user-test.dto";

import { Dialog } from "~/ui/shared/dialog";
import { Button } from "~/ui/shared/button";

import { ChecklistModal } from "./checklist-modal";

interface PerformanceModalProps {
  data: UserTestDto;
  performance: UserTestPerformanceDto[];
  onClose: () => void;
}

export const PerformanceModal = ({
  data,
  performance,
  onClose,
}: PerformanceModalProps) => {
  const score = performance.reduce((all, it) => all + it.score, 0);
  const contentRef = useRef<HTMLDivElement>(null);
  const onPrint = useReactToPrint({ contentRef });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="bg-white p-6 rounded-lg shadow-lg max-w-3xl">
        <Dialog.Title asChild>
          <h3 className="text-2xl font-semibold text-black">
            Desempenho
            {/* <span className="text-sm font-normal bg-alternative-1 rounded-lg w-14 h-6 ml-3 px-2">
            Clínica Médica
            </span> */}
          </h3>
        </Dialog.Title>

        <h2 className="font-semibold text-sm">{data.test.title}</h2>

        <div className="mt-4">
          <div ref={contentRef}>
            <section className="mb-6 flex justify-between items-center p-4 bg-gray-100 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="relative flex justify-center items-center">
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    className="w-16 h-16"
                  >
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      stroke="#d1d5db"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      stroke="#00C2CB"
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray="314"
                      strokeDashoffset={314 - (314 * score) / 10}
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <span className="absolute text-3xl font-semibold text-secondary-1">
                    {score % 1 === 0 ? score : score.toFixed(1)}
                  </span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">
                    Avaliação de desempenho
                  </h4>
                  <p className="text-gray-500 text-sm">
                    As notas abaixo dizem respeito ao seu desempenho no estudo
                    realizado.
                    <br />
                    Diferentes categorias constituem sua nota geral.
                    <br />
                    Analise em quais categorias você pode melhorar para aumentar
                    seu resultado geral.
                  </p>
                </div>
              </div>
            </section>

            <div className="flex justify-center space-y-3">
              <div className="flex flex-wrap items-center justify-center gap-8">
                {performance.map((it, i) => {
                  const percent = Math.floor((it.score * 100) / it.max_score);
                  return (
                    <div
                      key={it.name}
                      className="flex flex-col items-center space-y-2"
                    >
                      <div className="flex w-full">
                        {/* Barra de Progresso */}
                        <div className="w-8 mx-auto bg-gray-200 h-32 rounded-lg flex flex-col-reverse">
                          <div
                            data-even={i % 2 === 0}
                            className="h-full rounded-lg bg-alternative-2 data-[even=true]:bg-primary-1"
                            style={{ height: `${percent}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs font-medium">{it.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-flow-col gap-2">
            <ChecklistModal
              title={data.test.title}
              questions={data.test.test_questions}
              answers={data.test.selected_answers}
              trigger={
                <Button variant="primary-2" className="w-full h-11">
                  Ver Detalhes
                </Button>
              }
            />
            <Button variant="alternative-2" className="w-full h-11">
              Tentar Novamente
            </Button>
            <Button
              onClick={() => onPrint()}
              variant="primary-1"
              className="w-full h-11"
            >
              Salvar PDF
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
